import { AppointmentTypes, AvailabilityTypes } from 'common';
import { useInjection } from 'inversify-react';
import { useEffect } from 'react';

import { WizardStep } from 'domain/entities/WizardStep';
import { AsyncHookResult, InjectableHook, useHookInjection } from 'domain/hooks';

// RTK Query lazy trigger function type
type LazyQueryTrigger<TArgs, TResult> = (args: TArgs) => {
  unwrap(): Promise<TResult>;
  abort(): void;
  unsubscribe(): void;
  requestId: string;
};

import {
  BookingWizardNavigationHook,
  IBookingWizardNavigationHook,
} from './useCaseNavigateBookingWizard';

export enum GetInstructorsError {
  Unknown = 'unknown',
  Unauthorized = 'unauthorized',
}

export enum FirstTimeLessonError {
  Unknown = 'unknown',
  Unauthorized = 'unauthorized',
}

export const GetInstructorsAdapter = Symbol('GetInstructorsAdapter');

export type IGetInstructorsAdapter = InjectableHook<
  AsyncHookResult<Array<AvailabilityTypes.LessonAppointmentAvailability>, GetInstructorsError> & {
    saveSelectedInstructorToStore: (staffId: number) => void;
    setFlowTypeToLesson: () => void;
    flowType: string | undefined;
    selectFirstLessonFlow: () => void;
    clearPartySize: () => void;
    firstTimeLessonsLoading: boolean;
    firstTimeLessons: AvailabilityTypes.FirstTimeLessonAppointmentAvailability[] | undefined;
    firstTimeLessonError: FirstTimeLessonError | undefined;
    getFirstTimeLessonAppointmentAvailabilities: LazyQueryTrigger<
      {
        locationId: string;
        startDateTime?: string;
        endDateTime?: string;
        email?: string;
      },
      AvailabilityTypes.FirstTimeLessonAppointmentAvailability[]
    >;
    getInstructors: LazyQueryTrigger<
      {
        locationId: string;
        startDateTime?: string;
        endDateTime?: string;
        email?: string;
      },
      AvailabilityTypes.LessonAppointmentAvailability[]
    >;
  }
>;

const useCaseSelectInstructor = (currentStep?: WizardStep) => {
  const adapter = useInjection<IGetInstructorsAdapter>(GetInstructorsAdapter);

  // Always call the hook, even if you don't need it right away
  const bookingWizardNavigation = useHookInjection<IBookingWizardNavigationHook>(
    BookingWizardNavigationHook,
  );

  const {
    error,
    result,
    inProgress,
    firstTimeLessons,
    firstTimeLessonsLoading,
    firstTimeLessonError,
    saveSelectedInstructorToStore,
    setFlowTypeToLesson,
    selectFirstLessonFlow,
    clearPartySize,
    getFirstTimeLessonAppointmentAvailabilities,
    getInstructors,
    flowType,
  } = adapter();

  const handleNextStep = (staffId: number | null, isFirstTimeLesson?: boolean) => {
    clearPartySize();
    if (isFirstTimeLesson) {
      selectFirstLessonFlow();
      // Navigate to next step for first time lessons
      if (bookingWizardNavigation && currentStep) {
        bookingWizardNavigation.redirectToNextStep(currentStep);
      }
    } else if (staffId) {
      saveSelectedInstructorToStore(staffId);
      // Navigate directly to choose lesson time for regular lessons
      bookingWizardNavigation.redirectToStep(WizardStep.chooseLessonTime);
    }
  };

  useEffect(() => {
    // Ensure the flow type is set to Lesson if it hasn't been already
    const isLesson =
      flowType !== undefined &&
      [
        AppointmentTypes.BookingWizardFlowTypes.Lesson,
        AppointmentTypes.BookingWizardFlowTypes.FirstLesson,
      ].includes(flowType as AppointmentTypes.BookingWizardFlowTypes);

    if ((!flowType || !isLesson) && currentStep !== WizardStep.chooseLessonTime) {
      setFlowTypeToLesson();
    }
  }, [flowType, setFlowTypeToLesson, currentStep]);

  return {
    inProgress,
    error,
    result,
    firstTimeLessons,
    firstTimeLessonsLoading,
    firstTimeLessonError,
    getFirstTimeLessonAppointmentAvailabilities,
    getInstructors,
    nextStep: handleNextStep,
    prevStep: () => {
      if (bookingWizardNavigation && currentStep) {
        bookingWizardNavigation.redirectToPrevStep(currentStep);
      }
    },
  };
};

export default useCaseSelectInstructor;
